import { getContactsFromRest } from '../services/contacts-rest.service';
import type { ContactsRestFilters, GetContactsRestParams } from '../types/contacts-api.types';
import type { Contact, ContactsPagingResult } from '../types/contacts.types';

export const CONTACT_EXPORT_PAGE_SIZE = 50;

/** Safety bound before totalCount is known; replaced by total-derived ceiling. */
const CONTACT_EXPORT_PAGE_SAFETY_CEILING = 1_000_000;

export type ContactExportSnapshot = {
  q?: string;
  filter?: ContactsRestFilters;
  suppressed?: boolean;
};

export type ContactExportProgress = {
  fetched: number;
  total: number | null;
};

export type ContactExportRunOptions = {
  snapshot: ContactExportSnapshot;
  isCancelled?: () => boolean;
  onProgress?: (progress: ContactExportProgress) => void;
  fetchPage?: (params: GetContactsRestParams) => Promise<ContactsPagingResult>;
};

export type ContactExportRunResult =
  | { status: 'completed'; contacts: Contact[]; total: number }
  | { status: 'cancelled'; contacts: Contact[]; fetched: number }
  | { status: 'failed'; contacts: Contact[]; fetched: number; error: Error };

export async function runContactExport(
  options: ContactExportRunOptions
): Promise<ContactExportRunResult> {
  const fetchPage = options.fetchPage ?? getContactsFromRest;
  const contacts: Contact[] = [];
  let total: number | null = null;
  let maxPages = CONTACT_EXPORT_PAGE_SAFETY_CEILING;

  try {
    for (let pageNo = 1; pageNo <= maxPages; pageNo++) {
      if (options.isCancelled?.()) {
        return { status: 'cancelled', contacts, fetched: contacts.length };
      }

      const result = await fetchPage({
        q: options.snapshot.q,
        filter: options.snapshot.filter,
        ...(options.snapshot.suppressed !== undefined
          ? { suppressed: options.snapshot.suppressed }
          : {}),
        pageNo,
        pageSize: CONTACT_EXPORT_PAGE_SIZE,
      });

      if (total === null) {
        total = result.totalCount;
        maxPages = Math.ceil(total / CONTACT_EXPORT_PAGE_SIZE) + 1;
      }

      if (result.items.length === 0 && result.hasNextPage) {
        return {
          status: 'failed',
          contacts,
          fetched: contacts.length,
          error: new Error('Export stopped: empty page returned while more pages were claimed'),
        };
      }

      contacts.push(...result.items);
      options.onProgress?.({ fetched: contacts.length, total });

      if (options.isCancelled?.()) {
        return { status: 'cancelled', contacts, fetched: contacts.length };
      }

      if (!result.hasNextPage) {
        return {
          status: 'completed',
          contacts,
          total: total ?? contacts.length,
        };
      }
    }

    return {
      status: 'failed',
      contacts,
      fetched: contacts.length,
      error: new Error(
        `Export stopped: exceeded expected page limit (${maxPages}) while more pages were claimed`
      ),
    };
  } catch (err) {
    const error = err instanceof Error ? err : new Error('Export failed');
    return { status: 'failed', contacts, fetched: contacts.length, error };
  }
}
